import { FC, Fragment, useCallback, useMemo, useState } from "react";
import { ListItemIcon, ListItemText, MenuItem } from "@mui/material";
import { GppBadOutlined, VerifiedUserOutlined } from "@mui/icons-material";
import { useAppAbility } from "@/services/permissions";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { useDialog } from "@/utils/hooks/useDialog.hook";
import { gridResourcesProtectDisabledSelector } from "@/store/resources-grid/selectors/grid/selection/checkers/gridResourcesProtectDisabledSelector";
import { selectedResourcesAccountIdsSelector } from "@/store/resources-grid/selectors/grid/selection/account/selectedResourcesAccountIdsSelector";
import { ConfirmationDialog } from "@/components/common/modals/ConfirmationDialog";
import { selectedResourcesByMasterIdSelector } from "@/store/resources-grid/selectors/grid/selection/selectedResourcesByMasterIdSelector";
import { gridSelectedResourcesProtectThunk } from "@/store/resources-grid/thunks/actions/gridSelectedResourcesProtectThunk";
import { selectedResourcesProtectLoadingSelector } from "@/store/resources-grid/selectors/protection/selectedResourcesProtectLoadingSelector";
import { selectedResourcesResourceTypeByMasterIdSelector } from "@/store/resources-grid/selectors/grid/selection/selectedResourcesResourceTypeByMasterIdSelector";
import { getResourceCleanActionOptions } from "@/utils/clean-options";
import { liveUsageMgmtAccountsVisibilitiesByResourceTypeSelector } from "@/store/live-usage-mgmt/selectors/accounts-data/liveUsageMgmtAccountsVisibilitiesByResourceTypeSelector";
import { gridResourceDataPayloadSelector } from "@/store/resources-grid/selectors/grid/gridResourceDataPayloadSelector";
import { getProviderTypeByResourceType } from "@/utils/helpers/providers/getProviderTypeByResourceType";

interface LiveUsageResourceGridProtectActionProps {
  onClose(): void;
  masterGridId: string;
}

export const LiveUsageResourceGridProtectAction: FC<
  LiveUsageResourceGridProtectActionProps
> = ({ masterGridId, onClose }) => {
  const dispatch = useAppDispatch();
  const { cannot } = useAppAbility();
  const canNotCleanResource = cannot("protect", "resource");

  const [protectAction, setProtectAction] = useState<"protect" | "unprotect">();
  const { open: dialogOpen, openDialog, closeDialog } = useDialog();

  const protectDisabled = useAppSelector((state) =>
    gridResourcesProtectDisabledSelector(state, masterGridId),
  );

  const payload = useAppSelector((state) =>
    gridResourceDataPayloadSelector(state, masterGridId),
  );

  const selectedResources = useAppSelector((state) =>
    selectedResourcesByMasterIdSelector(state, masterGridId),
  );

  const accountIds = useAppSelector((state) =>
    selectedResourcesAccountIdsSelector(state, masterGridId),
  );

  const loading = useAppSelector((state) =>
    selectedResourcesProtectLoadingSelector(state, masterGridId),
  );
  const selectedResourcesResourceType = useAppSelector((state) =>
    selectedResourcesResourceTypeByMasterIdSelector(state, masterGridId),
  );

  const visibility = useAppSelector((state) =>
    selectedResourcesResourceType
      ? liveUsageMgmtAccountsVisibilitiesByResourceTypeSelector(
          state,
          selectedResourcesResourceType,
          payload?.accountIds,
          getProviderTypeByResourceType(selectedResourcesResourceType),
        )
      : undefined,
  );

  const hasAnyActions = useMemo(() => {
    if (!visibility?.actions || !selectedResourcesResourceType) {
      return false;
    }

    return !!getResourceCleanActionOptions(
      selectedResourcesResourceType,
    ).filter((option) => {
      return visibility?.actions?.[option.value] !== null;
    })?.length;
  }, [selectedResourcesResourceType, visibility]);

  const { hideProtect, hideUnProtect } = useMemo(() => {
    return {
      hideProtect:
        !hasAnyActions ||
        selectedResources?.every((resource) => resource?.is_protected),
      hideUnProtect:
        !hasAnyActions ||
        selectedResources?.every((resource) => !resource?.is_protected),
    };
  }, [hasAnyActions, selectedResources]);

  const onProtectItemClick = useCallback(() => {
    setProtectAction("protect");
    openDialog();
  }, [setProtectAction, openDialog]);

  const onUnprotectItemClick = useCallback(() => {
    setProtectAction("unprotect");
    openDialog();
  }, [setProtectAction, openDialog]);

  const closeHandler = useCallback(() => {
    closeDialog();
    onClose();
  }, [onClose, closeDialog]);

  const onProtectDialogConfirm = useCallback(async () => {
    if (!protectAction || !accountIds || !selectedResourcesResourceType) {
      return;
    }

    await dispatch(
      gridSelectedResourcesProtectThunk({
        masterGridId,
        actionType: protectAction,
        resourceType: selectedResourcesResourceType,
      }),
    );

    closeHandler();
  }, [
    dispatch,
    closeHandler,
    protectAction,
    masterGridId,
    accountIds,
    selectedResourcesResourceType,
  ]);

  const isProtected = protectAction !== "protect";

  return (
    <Fragment>
      {!hideProtect && (
        <MenuItem
          dense
          onClick={onProtectItemClick}
          disabled={protectDisabled || canNotCleanResource}
        >
          <ListItemIcon>
            <VerifiedUserOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Protect</ListItemText>
        </MenuItem>
      )}

      {!hideUnProtect && (
        <MenuItem
          dense
          onClick={onUnprotectItemClick}
          disabled={protectDisabled || canNotCleanResource}
        >
          <ListItemIcon>
            <GppBadOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Unprotect</ListItemText>
        </MenuItem>
      )}

      {dialogOpen && (
        <ConfirmationDialog
          open
          title={!isProtected ? "Enable Protection?" : "Disable Protection?"}
          dialogContent={
            !isProtected
              ? "Resources with Enabled Protection will be untouched by Cloudchipr."
              : "Users with sufficient permissions can take actions on resources with Disabled Protection."
          }
          CancelButtonProps={{
            onClick: closeHandler,
          }}
          ConfirmButtonProps={{
            children: !isProtected ? "Enable" : "Disable",
            onClick: onProtectDialogConfirm,
            loading: loading,
          }}
          onClose={closeHandler}
        />
      )}
    </Fragment>
  );
};
